<template>
  <div>
    <!-- Resumen por ventana. Se calcula siempre sobre el portafolio completo,
         así que la brecha no baja al filtrar. -->
    <v-row v-if="summary" class="mb-2">
      <v-col
        v-for="w in summaryWindows"
        :key="w.key"
        cols="6"
        sm="4"
        md="2"
      >
        <v-card
          outlined
          class="pa-3 text-center window-card"
          :class="{ 'window-card--active': window === w.key }"
          :style="{ borderColor: w.color }"
          @click="toggleWindow(w.key)"
        >
          <div class="window-label" :style="{ color: w.color }">{{ w.label }}</div>
          <div class="window-count">{{ w.contracts }}</div>
          <div class="window-mrr">{{ w.contracts ? formatMrr(w.mrr) : '—' }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- La brecha se muestra aparte y no como una ventana más: no es urgencia,
         es un dato que falta. -->
    <v-alert
      v-if="summary && summary.missingEndDate > 0"
      dense
      text
      type="warning"
      class="gap-alert mb-3"
    >
      <strong>{{ summary.missingEndDate }}</strong> de {{ summary.totalActive }} contratos activos
      no tienen fecha de término cargada, así que no aparecen en ninguna ventana de vencimiento.
      <a class="gap-link" @click="toggleWindow('noEndDate')">Revisarlos</a>
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      :items-per-page="20"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
      :no-data-text="emptyText"
      disable-sort
    >
      <template v-slot:top>
        <v-row class="px-4 pt-4 pb-2 align-center" style="gap: 12px;">
          <v-col cols="12" md="5" class="my-0 py-0">
            <v-text-field
              v-model="search"
              label="Buscar empresa o ID"
              dense hide-details outlined clearable
              append-icon="mdi-magnify"
              @input="debouncedFetch"
            />
          </v-col>
          <v-col cols="auto" class="my-0 py-0">
            <v-btn v-if="window" small outlined color="primary" @click="toggleWindow(window)">
              Ver todos
            </v-btn>
          </v-col>
          <v-spacer />
          <v-col cols="auto" class="my-0 py-0">
            <span v-if="summary && summary.snapshotDate" class="snapshot-note">
              MRR al {{ summary.snapshotDate | date }}
            </span>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.company="{ item }">
        <div class="font-weight-medium">{{ item.company }}</div>
        <div class="row-sub">{{ item.codigo }}<span v-if="item.producto"> — {{ item.producto }}</span></div>
      </template>

      <template v-slot:item.endDate="{ item }">
        <span v-if="item.endDate">{{ item.endDate | date }}</span>
        <span v-else class="grey--text">sin fecha de término</span>
      </template>

      <template v-slot:item.daysRemaining="{ item }">
        <v-chip
          v-if="item.daysRemaining !== null"
          small
          :color="windowColor(item.window)"
          class="white--text font-weight-bold"
        >
          {{ daysLabel(item) }}
        </v-chip>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.mrr="{ item }">
        <span v-if="item.mrr !== null">{{ formatMrr(item.mrr, item.mrrCurrency) }}</span>
        <span v-else class="grey--text" title="Sin dato en el snapshot de MRR">sin dato</span>
      </template>

      <template v-slot:item.adoption="{ item }">
        <div>{{ item.activeMemberships }} / {{ item.loadedUsers }}</div>
        <!-- Un contrato con cuota vendida y cero activas nunca arrancó: la
             conversación comercial es rescatarlo, no renovarlo. -->
        <v-chip v-if="neverStarted(item)" x-small color="red darken-1" dark class="mt-1">
          nunca arrancó
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <!-- Renovar solo tiene sentido sobre un contrato vigente: uno ya
             churneado o pausado se resuelve en el mantenedor, no acá. -->
        <v-btn
          v-if="item.status === 'Activo'"
          small
          text
          color="success"
          @click="openRenew(item)"
        >
          Renovar
        </v-btn>
        <v-btn small text color="primary" @click="openContrato(item)">
          Ver contrato
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="renewDialog" max-width="480">
      <v-card v-if="renewTarget">
        <v-card-title class="text-uppercase">Renovar contrato</v-card-title>
        <hr />
        <v-card-text class="pt-4">
          <p>
            Se va a crear el contrato que sucede a
            <strong>{{ renewTarget.codigo }}</strong> ({{ renewTarget.company }}),
            copiando sus planes, dominios, descuentos y condiciones.
          </p>
          <p class="mb-4">
            El contrato actual queda cerrado como <strong>renovado</strong>, no
            como baja, así que no cuenta en el churn.
          </p>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="renewForm.startDate"
                label="Inicio"
                type="date"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <!-- Duración en meses y no fecha de término: la fecha esconde
                   cuánto dura el contrato, que es justamente lo que se
                   negocia. Ojo, no es la vigencia de las membresías. -->
              <v-text-field
                v-model.number="renewForm.termMonths"
                label="Duración (meses)"
                type="number"
                min="1"
                max="120"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense class="mt-1">
            <v-col cols="12">
              <!-- De este monto salen las filas de facturación del contrato
                   nuevo, así que acá entra el precio renegociado y no el
                   anterior. -->
              <v-text-field
                v-model.number="renewForm.netAmount"
                label="Monto neto"
                type="number"
                min="0"
                :suffix="renewTarget.mrrCurrency || ''"
                :loading="loadingRenewTarget"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <p class="caption grey--text mt-2 mb-0">
            Termina el {{ renewEndPreview }}. Con este monto se generan las
            filas de facturación del período. Después de crearlo se abre para
            que ajustes planes y condiciones.
          </p>
        </v-card-text>
        <hr />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="renewDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            color="success"
            dark
            rounded
            :loading="renewing"
            @click="confirmRenew"
          >
            Renovar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ContractRenewalsService from '@/services/contract-renewals.service';
import ContratoService from '@/services/contrato.service';
import { utilsMixin } from '@/mixins/utilsMixin';

// Los umbrales viven en el backend; acá solo se traducen a etiqueta y color.
const WINDOW_META = {
  expired: { label: 'Vencidos', color: '#C62828' },
  due30: { label: '≤ 30 días', color: '#E65100' },
  due60: { label: '31–60 días', color: '#EF6C00' },
  due90: { label: '61–90 días', color: '#F9A825' },
  beyond90: { label: '> 90 días', color: '#2E7D32' },
  noEndDate: { label: 'Sin fecha', color: '#8593A3' },
};

export default {
  name: 'ContractRenewalsTable',
  mixins: [utilsMixin],

  data() {
    return {
      loading: false,
      rows: [],
      summary: null,
      window: '',
      search: '',
      searchTimer: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green',
      renewDialog: false,
      renewTarget: null,
      renewing: false,
      renewForm: { startDate: '', termMonths: 12, netAmount: null },
      loadingRenewTarget: false,
    };
  },

  computed: {
    headers() {
      return [
        { text: 'Empresa', value: 'company' },
        { text: 'Línea de negocio', value: 'lineaNegocio' },
        { text: 'Vencimiento', value: 'endDate' },
        { text: 'Faltan', value: 'daysRemaining' },
        { text: 'MRR', value: 'mrr' },
        { text: 'Activas / cargados', value: 'adoption' },
        { text: '', value: 'actions', align: 'end' },
      ];
    },

    /** Fecha de término resultante, para que se vea antes de confirmar. */
    renewEndPreview() {
      const { startDate, termMonths } = this.renewForm;
      if (!startDate || !termMonths || termMonths < 1) return '—';
      const end = new Date(`${startDate}T00:00:00Z`);
      end.setUTCMonth(end.getUTCMonth() + Number(termMonths));
      end.setUTCDate(end.getUTCDate() - 1);
      return end.toISOString().slice(0, 10);
    },

    summaryWindows() {
      if (!this.summary) return [];
      return Object.keys(WINDOW_META).map((key) => ({
        key,
        label: WINDOW_META[key].label,
        color: WINDOW_META[key].color,
        contracts: this.summary.byWindow[key] ? this.summary.byWindow[key].contracts : 0,
        mrr: this.summary.byWindow[key] ? this.summary.byWindow[key].mrr : 0,
      }));
    },

    emptyText() {
      if (this.loading) return 'Cargando...';
      return this.window
        ? 'No hay contratos en esta ventana'
        : 'No hay contratos para mostrar';
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    windowColor(w) {
      return (WINDOW_META[w] || {}).color || '#8593A3';
    },

    daysLabel(item) {
      if (item.daysRemaining === null) return '—';
      if (item.daysRemaining < 0) return `vencido hace ${Math.abs(item.daysRemaining)} d`;
      if (item.daysRemaining === 0) return 'vence hoy';
      return `${item.daysRemaining} d`;
    },

    // Ausencia de MRR no es cero: un contrato fuera del snapshot no factura
    // cero, simplemente no se sabe cuánto factura.
    formatMrr(value, currency) {
      if (value === null || value === undefined) return 'sin dato';
      const n = Math.round(Number(value) || 0).toLocaleString('es-CL');
      return currency ? `${n} ${currency}` : `$${n}`;
    },

    neverStarted(item) {
      return (item.membershipQuota || 0) > 0 && item.activeMemberships === 0;
    },

    toggleWindow(key) {
      this.window = this.window === key ? '' : key;
      this.fetchData();
    },

    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchData(), 400);
    },

    // `codigo` abre directo la edición de ese contrato en el mantenedor, que
    // es donde se carga la fecha de término faltante.
    openContrato(item) {
      this.$router.push({ name: 'Contratos', query: { codigo: item.codigo } });
    },

    openRenew(item) {
      this.renewTarget = item;
      // Se prellena con el término real del contrato anterior para que se vea
      // antes de confirmar. `duracionMeses` NO sirve para esto: ese campo es
      // la vigencia de las membresías, y hay contratos de 36 meses con
      // membresías de 12.
      this.renewForm = {
        startDate: this.dayAfter(item.endDate),
        termMonths: this.termMonths(item.startDate, item.endDate) ?? 12,
        netAmount: null,
      };
      this.renewDialog = true;
      this.loadRenewNetAmount(item.contratoId);
    },

    /**
     * El monto neto no viaja en la fila de vencimientos, así que se trae del
     * contrato. Si falla, el campo queda vacío y el backend hereda el monto
     * anterior — se prefiere no mostrar un número inventado.
     */
    async loadRenewNetAmount(contratoId) {
      this.loadingRenewTarget = true;
      const res = await ContratoService.getById(contratoId);
      this.loadingRenewTarget = false;
      if (res.status === 'ok' && this.renewDialog) {
        this.renewForm.netAmount = res.data?.netAmount ?? null;
      }
    },

    /** Día siguiente a una fecha `YYYY-MM-DD`; hoy si no hay fecha. */
    dayAfter(date) {
      const base = date ? new Date(`${date}T00:00:00Z`) : new Date();
      if (date) base.setUTCDate(base.getUTCDate() + 1);
      return base.toISOString().slice(0, 10);
    },

    /**
     * Meses enteros que abarca un término, o null si no son enteros. Un
     * contrato que va del 1 de agosto al 15 de julio no dura "12 meses", y
     * decir que sí sería peor que no decir nada.
     */
    termMonths(startDate, endDate) {
      if (!startDate || !endDate) return null;
      const start = new Date(`${startDate}T00:00:00Z`);
      const end = new Date(`${endDate}T00:00:00Z`);
      end.setUTCDate(end.getUTCDate() + 1);
      if (end.getUTCDate() !== start.getUTCDate()) return null;
      const months =
        (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
        (end.getUTCMonth() - start.getUTCMonth());
      return months > 0 ? months : null;
    },

    async confirmRenew() {
      this.renewing = true;
      const payload = {};
      if (this.renewForm.startDate) payload.startDate = this.renewForm.startDate;
      if (this.renewForm.termMonths) payload.termMonths = Number(this.renewForm.termMonths);
      if (this.renewForm.netAmount !== null && this.renewForm.netAmount !== '') {
        payload.netAmount = Number(this.renewForm.netAmount);
      }

      const res = await ContratoService.renew(this.renewTarget.contratoId, payload);
      this.renewing = false;

      if (res.status !== 'ok') {
        // Se deja el diálogo abierto: si el backend rechazó la duración, el
        // usuario corrige y reintenta sin volver a buscar el contrato.
        this.showErrorSnackbar(res.message || 'No se pudo renovar el contrato');
        return;
      }

      this.renewDialog = false;
      this.showSuccessSnackbar(`Contrato renovado: ${res.data.codigo}`);
      // El contrato clonado es un punto de partida, no el resultado final: en
      // la renovación se renegocian planes y condiciones, así que se abre
      // directamente en el mantenedor para ajustarlo.
      this.$router.push({ name: 'Contratos', query: { codigo: res.data.codigo } });
    },

    async fetchData() {
      this.loading = true;
      const res = await ContractRenewalsService.getRenewals({
        window: this.window,
        search: this.search,
      });
      if (res.status === 'ok') {
        this.rows = res.data.rows;
        this.summary = res.data.summary;
      } else {
        this.rows = [];
        this.showErrorSnackbar('Ocurrió un error al cargar las renovaciones');
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.window-card {
  border-radius: 8px;
  cursor: pointer;
  height: 100%;
}
.window-card--active {
  border-width: 2px;
}
.window-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  font-family: Inter, sans-serif;
}
.window-count {
  font-size: 22px;
  font-weight: 700;
  color: #1B1D21;
  font-family: Inter, sans-serif;
}
.window-mrr {
  font-size: 11px;
  color: #8593A3;
  font-family: Inter, sans-serif;
}
.gap-alert {
  font-size: 13px;
  font-family: Inter, sans-serif;
}
.gap-link {
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}
.row-sub {
  font-size: 11px;
  color: #8593A3;
}
.snapshot-note {
  font-size: 12px;
  color: #8593A3;
  font-family: Inter, sans-serif;
}
</style>
